import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LendyIcon from "../components/lendy/LendyIcon";
import { defaultThreadId } from "../models/askAddyV2";
import { ChatTile, getWhen, useDrawerData } from "./V2ChatsDrawer";
import "./v2ChatSearch.css";

const RECENT_COUNT = 8;

/**
 * Search chats as a popup over the chat (after Muse): the recent chats first, then the chats whose
 * title matches as you type. Arrow keys move, Enter opens, Escape or a click outside closes.
 */
const V2ChatSearch = ({ uid, organizationId, loanId, scenarioId, activeThreadId, railWidth = 0, onThreadClick, onClose }) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);
  const { chats } = useDrawerData({ uid, organizationId, loanId, scenarioId, activeThreadId, isDrawerOpen: true });
  const needle = query.trim().toLowerCase();
  const matches = (chats || []).filter(({ title }) => !needle || (title || "New chat").toLowerCase().includes(needle));
  const shown = needle ? matches : matches.slice(0, RECENT_COUNT);
  const activeChat = shown[activeIndex];
  const defaultId = defaultThreadId({ organizationId, userId: uid, loanId, scenarioId });

  useEffect(() => {
    setActiveIndex(0);
  }, [needle]);

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const open = (thread) => {
    onThreadClick(thread.id === defaultId ? null : thread.id);
    onClose();
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      onClose();

      return;
    }
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && shown.length) {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index) => (index + step + shown.length) % shown.length);

      return;
    }
    if (event.key === "Enter" && activeChat) {
      open(activeChat);
    }
  };

  return createPortal(
    <div
      className="aav2-search-layer"
      style={{ paddingLeft: railWidth }}
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="aav2-search" role="dialog" aria-modal="true" aria-label="Search chats" onKeyDown={onKeyDown}>
        <label className="aav2-search-field">
          <LendyIcon name="search" size={18} />
          <input
            autoFocus
            placeholder="Search chats"
            aria-label="Search chats"
            role="combobox"
            aria-expanded="true"
            aria-controls="aav2-search-results"
            aria-activedescendant={activeChat ? `aav2-search-${activeChat.id}` : undefined}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="aav2-search-body">
          {shown.length > 0 && <div className="aav2-search-title">{needle ? "Chats" : "Recent chats"}</div>}
          <ul id="aav2-search-results" className="aav2-search-list" role="listbox" aria-label="Chats" ref={listRef}>
            {shown.map((thread, index) => (
              <li
                key={thread.id}
                id={`aav2-search-${thread.id}`}
                className="aav2-search-row"
                role="option"
                aria-selected={index === activeIndex}
                onMouseMove={() => setActiveIndex(index)}
                onClick={() => open(thread)}
              >
                <ChatTile isLoanChat={Boolean(thread.metadata?.loanId || (!thread.isLegacy && loanId))} />
                <span className="aav2-drawer-chat-text">
                  <b>{thread.title || "New chat"}</b>
                  <small>{getWhen(thread.updatedAt)}</small>
                </span>
              </li>
            ))}
          </ul>
          {chats === null && <p className="aav2-search-empty">Loading chats…</p>}
          {chats && !shown.length && <p className="aav2-search-empty">{needle ? "No chats match." : "No chats yet."}</p>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default V2ChatSearch;
